import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import {
  MessageSquare, Bot, Database, IndianRupee, TrendingUp, Download,
  ChevronRight, ChevronDown, Loader2, Copy, BarChart3, Layers, PieChart,
} from 'lucide-react';

// ── Indian number / currency formatting ─────────────────────────────────────
const inr = (n) =>
  typeof n === 'number'
    ? new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n)
    : '—';
const intIN = (n) => new Intl.NumberFormat('en-IN').format(Math.round(n || 0));
const compact = (n) => {
  const x = Number(n || 0);
  if (x >= 1e6) return `${(x / 1e6).toFixed(2)}M`;
  if (x >= 1e3) return `${(x / 1e3).toFixed(2)}K`;
  return intIN(x);
};

const RANGES = [
  { key: '7', label: 'Last 7 days', days: 7 },
  { key: '30', label: 'Last 30 days', days: 30 },
  { key: '90', label: 'Last 90 days', days: 90 },
];
const isoDaysAgo = (days) => {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - (days - 1));
  return d.toISOString().slice(0, 10);
};
const isoToday = () => new Date().toISOString().slice(0, 10);

const StatCard = ({ icon: Icon, label, value, sub, accent, testid }) => (
  <div className={`rounded-2xl border p-5 flex flex-col ${accent ? 'bg-emerald-50/60 border-emerald-200' : 'bg-white border-fab-primary-lighter'}`} data-testid={testid}>
    <div className="flex items-center gap-2.5 mb-3">
      <span className={`w-9 h-9 rounded-xl flex items-center justify-center ${accent ? 'bg-emerald-100 text-emerald-600' : 'bg-fab-primary-lighter/60 text-fab-primary'}`}>
        <Icon className="w-4.5 h-4.5" strokeWidth={2} />
      </span>
      <span className="text-[13px] font-medium text-fab-text-secondary">{label}</span>
    </div>
    <div className="text-[28px] font-bold text-fab-text leading-none tracking-tight" data-testid={`${testid}-value`}>{value}</div>
    {sub != null && <div className="text-[12px] text-fab-text-muted mt-2">{sub}</div>}
  </div>
);

const NT = <span className="text-fab-text-muted font-medium">Not tracked</span>;

export default function AdminMatrix() {
  const navigate = useNavigate();
  const [rangeKey, setRangeKey] = useState('30');
  const [rangeOpen, setRangeOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(false);
  const [showAll, setShowAll] = useState(false);

  const load = useCallback(() => {
    const days = (RANGES.find((r) => r.key === rangeKey) || RANGES[1]).days;
    setLoading(true); setErr(false);
    api.get('/admin/matrix', { params: { date_from: isoDaysAgo(days), date_to: isoToday() } })
      .then(({ data }) => setData(data))
      .catch(() => setErr(true))
      .finally(() => setLoading(false));
  }, [rangeKey]);

  useEffect(() => { load(); }, [load]);

  const t = data?.totals;
  const sellers = data?.sellers || [];
  const visible = showAll ? sellers : sellers.slice(0, 5);

  const exportCsv = () => {
    const rows = [
      ['Seller', 'Total Messages', 'AI Replies (Answered)', 'Deterministic Replies', 'Blocked Attempts',
       'Seller-Tool Operations', 'Input Tokens', 'Output Tokens', 'Total Tokens', 'Priced Cost (INR)',
       'Unpriced Tokens (in/out)', 'Avg Cost per AI Reply (INR)'],
      ...sellers.map((s) => [
        s.name, s.total_messages, s.ai_replies ?? 0, s.deterministic_replies ?? 0, s.blocked_attempts ?? 0,
        s.seller_tool_calls ?? 0,
        s.input_tokens, s.output_tokens, s.total_tokens,
        s.total_priced_cost_inr ?? 0,
        `${s.unpriced_input_tokens ?? 0}/${s.unpriced_output_tokens ?? 0}`,
        s.avg_cost_per_ai_reply != null ? s.avg_cost_per_ai_reply : 'Not tracked',
      ]),
    ];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `matrix_${data?.range?.from}_${data?.range?.to}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };


  if (loading) {
    return <div className="flex justify-center p-20" data-testid="matrix-loading"><Loader2 className="w-7 h-7 animate-spin text-fab-primary" /></div>;
  }
  if (err || !data) {
    return (
      <div className="p-6" data-testid="matrix-error">
        <div className="bg-white border border-rose-200 rounded-xl p-10 text-center">
          <div className="font-semibold text-fab-text">Unable to load the Matrix.</div>
          <button onClick={load} className="mt-4 px-5 py-2.5 text-sm font-semibold rounded-lg bg-fab-primary text-white">Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-[1400px]" data-testid="admin-matrix">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold text-fab-text" data-testid="matrix-title">Matrix</h1>
          <p className="text-[13px] text-fab-text-muted mt-1">Overview of AI messaging cost across the platform</p>
        </div>
        <div className="flex items-center gap-2.5">
          <div className="relative">
            <button onClick={() => setRangeOpen((o) => !o)} data-testid="matrix-range-toggle"
              className="flex items-center gap-2 px-3.5 py-2 rounded-lg border border-fab-primary-lighter bg-white text-[13px] font-medium text-fab-text">
              {(RANGES.find((r) => r.key === rangeKey) || RANGES[1]).label}
              <ChevronDown className="w-4 h-4 text-fab-text-muted" />
            </button>
            {rangeOpen && (
              <div className="absolute right-0 mt-1 w-40 bg-white border border-fab-primary-lighter rounded-lg shadow-lg z-10 overflow-hidden" data-testid="matrix-range-menu">
                {RANGES.map((r) => (
                  <button key={r.key} data-testid={`matrix-range-${r.key}`}
                    onClick={() => { setRangeKey(r.key); setRangeOpen(false); }}
                    className={`block w-full text-left px-3.5 py-2 text-[13px] hover:bg-fab-primary-lighter/40 ${r.key === rangeKey ? 'text-fab-primary font-semibold' : 'text-fab-text'}`}>
                    {r.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          <button onClick={exportCsv} data-testid="matrix-export"
            className="flex items-center gap-2 px-4 py-2 rounded-lg border border-fab-primary-lighter bg-white text-[13px] font-semibold text-fab-primary hover:bg-fab-primary-lighter/40">
            <Download className="w-4 h-4" /> Export
          </button>
        </div>
      </div>

      {/* Stat cards -- ticket 281089 Stage B3: the old "AI Messages"/"Avg. Cost
          per AI Message"/one-Sonnet-rate "Total Messaging Cost" are REPLACED
          (not shown alongside) by the correctly-classified, per-model-priced
          figures below. Legacy fields (ai_messages, cost_inr, ...) still come
          back from the API for other consumers, just not rendered here. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3.5 mt-5">
        <StatCard testid="matrix-card-total-messages" icon={MessageSquare} label="Total Messages" value={intIN(t.total_messages)} sub="web + WhatsApp" />
        <StatCard testid="matrix-card-ai-replies" icon={Bot} label="AI replies (answered)" value={intIN(t.ai_replies ?? 0)} sub={t.replies_unreconciled ? 'unreconciled' : 'sales_brain only'} />
        <StatCard testid="matrix-card-deterministic" icon={MessageSquare} label="Deterministic replies" value={intIN(t.deterministic_replies ?? 0)}
          sub="Incl. the fixed reply sent when an AI call was blocked/failed" />
        <StatCard testid="matrix-card-blocked" icon={Database} label="Blocked attempts" value={intIN(t.blocked_attempts ?? 0)} sub="₹0, model call failed/blocked" />
        <StatCard testid="matrix-card-seller-tools" icon={Layers} label="Seller-tool operations" value={intIN(t.seller_tool_calls ?? 0)} sub="poster/cover/catalog/OCR" />
        <StatCard testid="matrix-card-avg" icon={TrendingUp} accent label="Avg Cost per AI Reply" value={t.avg_cost_per_ai_reply != null ? inr(t.avg_cost_per_ai_reply) : 'Not tracked'} sub="Buyer-chat only, priced per call's model" />
      </div>
      <p className="text-[11px] text-fab-text-muted mt-1.5">
        Deterministic replies includes the automatic fallback reply sent when an AI call was blocked or failed.
        Reply counts are aggregated across the web chatbox and WhatsApp.
      </p>

      {/* Cost + unpriced usage */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 mt-3.5">
        <StatCard testid="matrix-card-cost" icon={IndianRupee} label="Total Messaging Cost" value={inr(t.total_priced_cost_inr ?? 0)} sub="Priced per call's own model (buyer-chat + seller-tool)" />
        <StatCard testid="matrix-card-unpriced" icon={Database} label="Unpriced usage"
          value={(t.unpriced_input_tokens || t.unpriced_output_tokens) ? `${compact(t.unpriced_input_tokens)} in / ${compact(t.unpriced_output_tokens)} out` : 'None'}
          sub={t.unpriced_models?.length ? `Price unverified: ${t.unpriced_models.join(', ')}` : 'All usage priced'} />
      </div>

      {/* Cost by Sellers */}
      <div className="mt-5 bg-white border border-fab-primary-lighter rounded-2xl overflow-hidden" data-testid="matrix-sellers">
        <div className="px-5 pt-5 pb-3 font-display text-lg font-bold text-fab-text">Cost by Sellers</div>
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-fab-primary-lighter/30 text-[12px] uppercase tracking-wide text-fab-text-secondary">
                <th className="px-5 py-3 font-semibold">Seller Name</th>
                <th className="px-4 py-3 font-semibold">Total Messages</th>
                <th className="px-4 py-3 font-semibold">AI Replies (Answered)</th>
                <th className="px-4 py-3 font-semibold">Deterministic Replies</th>
                <th className="px-4 py-3 font-semibold">Blocked Attempts</th>
                <th className="px-4 py-3 font-semibold">Seller-Tool Ops</th>
                <th className="px-4 py-3 font-semibold">Priced Cost (₹)</th>
                <th className="px-4 py-3 font-semibold">Avg Cost / AI Reply</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {visible.length === 0 && (
                <tr><td colSpan={9} className="px-5 py-8 text-center text-fab-text-muted text-[13px]" data-testid="matrix-empty">No messaging activity in this period.</td></tr>
              )}
              {visible.map((s) => (
                  <tr key={s.seller_id} onClick={() => navigate(`/dashboard/admin/sellers/${s.seller_id}`)}
                    className="border-t border-fab-primary-lighter/60 text-[13.5px] text-fab-text hover:bg-fab-primary-lighter/20 cursor-pointer"
                    data-testid={`matrix-seller-row-${s.seller_id}`}>
                    <td className="px-5 py-3.5 font-medium">{s.name}</td>
                    <td className="px-4 py-3.5">{intIN(s.total_messages)}</td>
                    <td className="px-4 py-3.5">{intIN(s.ai_replies)}</td>
                    <td className="px-4 py-3.5">{intIN(s.deterministic_replies)}</td>
                    <td className="px-4 py-3.5">{intIN(s.blocked_attempts)}</td>
                    <td className="px-4 py-3.5">{intIN(s.seller_tool_calls)}</td>
                    <td className="px-4 py-3.5">{inr(s.total_priced_cost_inr)}</td>
                    <td className="px-4 py-3.5">{s.avg_cost_per_ai_reply != null ? inr(s.avg_cost_per_ai_reply) : NT}</td>
                    <td className="px-4 py-3.5 text-fab-text-muted"><ChevronRight className="w-4 h-4" /></td>
                  </tr>
              ))}
            </tbody>
          </table>
        </div>
        {sellers.length > 5 && (
          <button onClick={() => setShowAll((v) => !v)} data-testid="matrix-view-all"
            className="w-full py-3 text-[13px] font-semibold text-fab-primary flex items-center justify-center gap-1.5 border-t border-fab-primary-lighter/60 hover:bg-fab-primary-lighter/20">
            {showAll ? 'Show less' : 'View all sellers'} <ChevronDown className={`w-4 h-4 transition-transform ${showAll ? 'rotate-180' : ''}`} />
          </button>
        )}
      </div>

      {/* Platform Cost Summary */}
      <div className="mt-5 bg-white border border-fab-primary-lighter rounded-2xl p-5" data-testid="matrix-platform-summary">
        <div className="font-display text-lg font-bold text-fab-text mb-4">Platform Cost Summary</div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          {[
            { icon: IndianRupee, label: 'Total Messaging Cost (priced)', value: inr(t.total_priced_cost_inr ?? 0) },
            { icon: BarChart3, label: 'Total Tokens Used', value: t.tokens_tracked ? compact(t.total_tokens) : 'Not tracked' },
            { icon: Copy, label: 'Input Tokens', value: t.tokens_tracked ? compact(t.input_tokens) : 'Not tracked' },
            { icon: Layers, label: 'Output Tokens', value: t.tokens_tracked ? compact(t.output_tokens) : 'Not tracked' },
            { icon: PieChart, label: 'AI Replies / Total Messages', value: t.total_messages ? `${((t.ai_replies / t.total_messages) * 100).toFixed(2)}%` : '0%' },
          ].map((it, i) => (
            <div key={i} className="flex items-center gap-3" data-testid={`matrix-summary-${i}`}>
              <span className="w-10 h-10 rounded-xl bg-fab-primary-lighter/60 text-fab-primary flex items-center justify-center"><it.icon className="w-4.5 h-4.5" /></span>
              <div>
                <div className="text-[12px] text-fab-text-muted">{it.label}</div>
                <div className="text-[16px] font-bold text-fab-text">{it.value}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <p className="text-center text-[12px] text-fab-text-muted mt-5">
        Note: Each AI call is priced at its OWN model's published rate (haiku/sonnet), never one
        flat rate. Gemini image-generation usage has no published rate yet and is shown separately
        as unpriced usage, never folded into the cost above. USD→INR {data.pricing.usd_inr}.
      </p>
    </div>
  );
}
